import { useState } from 'react';
import { useRouter } from 'next/router';
import Callout from '@/shared/components/flip-ui-kit/Callout';
import Checkbox from '@/shared/components/flip-ui-kit/Checkbox';
import { usePolkadot } from '@/shared/hooks/usePolkadot';
import useSettingsStore from '@/shared/hooks/useSettingsStore';
import CloseIcon from '@/shared/icons/flip-ui-kit/large/Close';
import CoinsIcon from '@/shared/icons/flip-ui-kit/large/Coins';
import EmojiHappyIcon from '@/shared/icons/flip-ui-kit/large/EmojiHappy';
import ShieldTickIcon from '@/shared/icons/flip-ui-kit/large/ShieldTick';
import { DashboardModal } from './Modal';
import { ReadonlyButton, ReadonlyHTMLButton, type ReadonlyHTMLButtonProps } from './Readonly';
import Button, { type ButtonProps } from '../flip-ui-kit/Button';

const useAccountFundingState = (accountIsEmpty: boolean = false) => {
  const { selectedAccount } = usePolkadot();
  const hideFundAccountModal = useSettingsStore((s) => s.hideFundAccountModal);

  if (!selectedAccount || selectedAccount.readonly)
    return {
      showFundAccountModal: false,
      showFundAccountCallout: false,
    };

  return {
    showFundAccountModal: accountIsEmpty && !hideFundAccountModal,
    showFundAccountCallout: accountIsEmpty && hideFundAccountModal,
  };
};

const FundAccountModal = ({
  open,
  onClose,
  onDeposit,
  subtitle,
}: {
  open: boolean;
  onClose: () => void;
  onDeposit: () => void;
  subtitle?: string;
}) => {
  const setSetting = useSettingsStore((s) => s.setSetting);
  const hideFundAccountModal = useSettingsStore((s) => s.hideFundAccountModal);

  const handleDeposit = () => {
    onClose();
    onDeposit();
  };

  return (
    <DashboardModal open={open} onClose={() => onClose()}>
      <div className="flex w-[510px] flex-col gap-6 rounded-md border border-cf-gray-4 bg-cf-gray-3 p-6 max-md:mx-1">
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-col gap-3">
            <div className="font-aeonikMedium text-20 text-cf-white">Deposit funds to continue</div>
            <div className="text-16 text-cf-light-2">
              {subtitle ?? 'Please fund your account first.'}
            </div>
          </div>
          <button
            type="button"
            className="shrink-0 transition hover:text-cf-white"
            onClick={() => onClose()}
            aria-label="Close modal"
          >
            <CloseIcon />
          </button>
        </div>
        <div className="flex flex-col gap-6 border-t border-cf-gray-4 pt-6">
          <div className="flex items-start gap-4">
            <CoinsIcon className="mt-0.5 shrink-0 text-cf-light-2" />
            <div className="flex flex-col gap-2">
              <div className="font-aeonikMedium text-16 text-cf-white">Choose an asset</div>
              <div className="text-16 text-cf-light-2">
                Select the asset you want to deposit (e.g. USDC, USDT, BTC, SOL).
              </div>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <ShieldTickIcon className="mt-0.5 shrink-0 text-cf-light-2" />
            <div className="flex flex-col gap-2">
              <div className="font-aeonikMedium text-16 text-cf-white">Add a refund address</div>
              <div className="text-16 text-cf-light-2">
                Provide a refund address. If the deposit can&apos;t be completed, your funds will be
                returned automatically.
              </div>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <EmojiHappyIcon className="mt-0.5 shrink-0 text-cf-light-2" />
            <div className="flex flex-col gap-2">
              <div className="font-aeonikMedium text-16 text-cf-white">Access all app features</div>
              <div className="text-16 text-cf-light-2">
                Once your deposit is received, all actions in the LP app will become available.
              </div>
            </div>
          </div>
        </div>
        <Button fullWidth type="primary-standard" onClick={handleDeposit}>
          Deposit funds
        </Button>
        <div className="flex items-center gap-x-2 font-aeonikMedium text-12 text-cf-light-3">
          <Checkbox
            id="dont-show-fund-account-modal"
            checked={hideFundAccountModal}
            setChecked={() => setSetting('hideFundAccountModal', !hideFundAccountModal)}
          />
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor="dont-show-fund-account-modal" className="w-fit cursor-pointer">
            Do not show this message again
          </label>
        </div>
      </div>
    </DashboardModal>
  );
};

export const FundAccountButton = ({
  fundingSubtitle,
  onDeposit,
  accountIsEmpty,
  ...props
}: ButtonProps & {
  fundingSubtitle?: string;
  onDeposit?: () => void;
  accountIsEmpty?: boolean;
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const { showFundAccountModal } = useAccountFundingState(accountIsEmpty);
  const router = useRouter();

  const handleClick: ButtonProps['onClick'] = (e) => {
    if (showFundAccountModal) {
      e.stopPropagation();
      setModalOpen(true);
      return;
    }
    props.onClick?.(e);
  };

  return (
    <>
      <ReadonlyButton {...props} onClick={handleClick} />
      <FundAccountModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onDeposit={onDeposit ?? (() => router.push('/balances'))}
        subtitle={fundingSubtitle}
      />
    </>
  );
};

export const FundAccountHTMLButton = ({
  fundingSubtitle,
  onDeposit,
  accountIsEmpty,
  ...props
}: ReadonlyHTMLButtonProps & {
  fundingSubtitle?: string;
  onDeposit?: () => void;
  accountIsEmpty?: boolean;
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const { showFundAccountModal } = useAccountFundingState(accountIsEmpty);
  const router = useRouter();

  const handleClick: ReadonlyHTMLButtonProps['onClick'] = (e) => {
    if (showFundAccountModal) {
      e.stopPropagation();
      setModalOpen(true);
      return;
    }
    props.onClick?.(e);
  };

  return (
    <>
      <ReadonlyHTMLButton {...props} onClick={handleClick} />
      <FundAccountModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onDeposit={onDeposit ?? (() => router.push('/balances'))}
        subtitle={fundingSubtitle}
      />
    </>
  );
};

export const FundAccountCallout = ({
  onFundAccount,
  accountIsEmpty,
}: {
  onFundAccount: () => void;
  accountIsEmpty?: boolean;
}) => {
  const { showFundAccountCallout } = useAccountFundingState(accountIsEmpty);

  if (!showFundAccountCallout) return null;

  return (
    <Callout type="info" fullWidth>
      Please{' '}
      <button type="button" className="underline" onClick={onFundAccount}>
        fund your account
      </button>{' '}
      first.
    </Callout>
  );
};
